import type { CSSProperties, ReactNode } from 'react'
import { isPlainLeftClick, navigate } from './routing'

/**
 * A link that stays inside the app.
 *
 * It is a real anchor with a real `href`, so the status bar shows where it
 * goes, middle-click opens a tab and the keyboard reaches it. The click handler
 * only takes over the one case the browser would otherwise reload for.
 */
export function RouteLink({
  to,
  children,
  style,
  className,
  ariaLabel,
  onNavigate,
}: {
  to: string
  children: ReactNode
  style?: CSSProperties
  /** For the rules a stylesheet owns — a hit area, a media query. */
  className?: string
  /**
   * What the link is called, when what it *reads* changes with the width.
   * Named rather than spread from `...rest` so the surface stays a list of
   * things a caller may pass.
   */
  ariaLabel?: string
  /** Runs after the path changes, for callers that have an overlay to close. */
  onNavigate?: () => void
}) {
  return (
    <a
      href={to}
      style={style}
      className={className}
      aria-label={ariaLabel}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return
        event.preventDefault()
        navigate(to)
        onNavigate?.()
      }}
    >
      {children}
    </a>
  )
}
